
import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { CalendarCheck, ListCheck } from "lucide-react";
import { TaskScheduleList } from "./TaskScheduleList";
import { CreateTaskSchedule } from "./CreateTaskSchedule";
import { UserWithRole } from "@/types/admin";

interface SchedulingToolProps {
  users: UserWithRole[];
}

export const SchedulingTool = ({ users }: SchedulingToolProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const { data: schedules, isLoading, error } = useQuery({
    queryKey: ["task-schedules", selectedDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_schedules")
        .select(`
          *,
          tasks (*),
          profiles!task_schedules_user_id_fkey (
            first_name,
            last_name
          )
        `)
        .gte("start_time", format(selectedDate, "yyyy-MM-dd"))
        .lte("start_time", format(selectedDate, "yyyy-MM-dd 23:59:59"));

      if (error) {
        console.error("Fejl ved hentning af opgaver:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log("Ingen opgaver fundet for den valgte dato");
      }

      return data || [];
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kalender</CardTitle>
            <CalendarCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={da}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Planlægning</CardTitle>
            <ListCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList>
                <TabsTrigger value="overview">Oversigt</TabsTrigger>
                <TabsTrigger value="create">Opret ny opgave</TabsTrigger>
              </TabsList>
              <TabsContent value="overview">
                <TaskScheduleList 
                  schedules={schedules || []} 
                  isLoading={isLoading}
                  selectedDate={selectedDate}
                />
              </TabsContent>
              <TabsContent value="create">
                <CreateTaskSchedule selectedDate={selectedDate} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
